import { format, isValid, parse } from 'date-fns'
import {
  ArrowBigDownDash,
  ArrowBigUp,
  ArrowBigUpDash,
  ArrowDown,
  ArrowRight,
  ArrowRightLeft,
  ArrowUp,
  ArrowUpFromDot,
  ChevronDown,
  ChevronsDown,
  ChevronsUpDown,
  Circle,
  CircleCheck,
  CircleChevronDown,
  CircleMinus,
  CircleX,
  ClockAlert,
  ClockArrowDown,
  ClockArrowUp,
  CloudAlert,
  Eye,
  FileWarning,
  GalleryThumbnails,
  History,
  Hourglass,
  Mail,
  MessageCircle,
  NotepadText,
  OctagonAlert,
  OctagonX,
  Phone,
  ShieldAlert,
  ShieldBan,
  Siren,
  SquarePlus,
  Timer,
  TimerOff,
  TimerReset,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Webhook,
} from 'lucide-vue-next'
import mitt from 'mitt'
import { storeToRefs } from 'pinia'
import { twMerge } from 'tailwind-merge'
import { type Component } from 'vue'
import { type RouteLocationNormalized, type RouteLocationNormalizedLoaded } from 'vue-router'
import { useToast } from '@/foundation/toast/use-toast'
import { useTeamStore } from '@/stores/teams'
import { useTicketStore } from '@/stores/tickets'
import type { Assignee } from '@/views/companies/data/schema'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatTime(minutesStr: string) {
  const totalMinutes = parseInt(minutesStr, 10)

  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60

  let result = ''

  if (days > 0) {
    result += `${days} day${days > 1 ? 's' : ''} `
  }
  if (hours > 0) {
    result += `${hours} hour${hours > 1 ? 's' : ''} `
  }
  if (minutes > 0 || result === '') {
    result += `${minutes} minute${minutes > 1 ? 's' : ''}`
  }

  return result.trim()
}

export function debounce(func: (...args: any[]) => any, timeout = 300) {
  let timer: ReturnType<typeof setTimeout>

  return (...args: unknown[]) => {
    clearTimeout(timer)

    timer = setTimeout(() => {
      func(...args)
    }, timeout)
  }
}

export const getInitials = (name: string) => {
  if (!name) return ''
  const initials = name.slice(0, 2).toUpperCase()

  return initials
}

export const formatFullDate = (date: any) => {
  if (!date) return ''

  return format(new Date(date), 'MMM d, yyyy | h:mm a')
}

export const formatDate = (date: any) => {
  if (!date) return ''

  let parsedDate = new Date(date)

  if (isNaN(parsedDate.getTime()) && typeof date === 'string' && date.includes('/')) {
    parsedDate = parse(date, 'dd/MM/yyyy', new Date())
  }

  if (!isValid(parsedDate)) return ''

  return format(parsedDate, 'MMM d, yyyy')
}

export const formatDateTime = (date: any) => {
  if (!date) return ''

  return format(new Date(date), 'h:mm a')
}

export const mapWithAccessorKey = (items: any[], accessorKey: string) => {
  return items.map((item) => ({
    ...item,
    label: item.name,
    value: item.name,
    accessorKey,
  }))
}

export function formatDateSlashToLong(dateStr: string): string {
  const [day, month, year] = dateStr.split('/')
  const date = new Date(Number(year), Number(month) - 1, Number(day))

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function resolveQueueTitle(
  route: RouteLocationNormalized,
  queues: { id: string; title: string; slug: string; companyId: string; filters: string }[],
): string {
  const queue = queues.find((q) => q.slug === route.params.queueSlug)

  if (queue) {
    return queue.title
  }

  return `Queue ${route.params.queueSlug}`
}

export function resolveTicketTitle(
  route: RouteLocationNormalized,
  queues: { id: string; title: string; slug: string; companyId: string; filters: string }[],
): string {
  // find the ticket by id in the ticket store
  const ticketStore = useTicketStore()
  const { tickets } = storeToRefs(ticketStore)
  const foundTicket = tickets.value.find((t) => t.id === route.params.ticketId)

  if (foundTicket) {
    // Display ticket number and title if available
    const number = foundTicket.ticketNumber ?? route.params.ticketId
    const title = foundTicket.title ?? ''

    return title ? `${number} | ${title}` : `${number}`
  }

  // Final fallback to query or id

  return route.query.ticket ? `${route.query.ticket}` : `Ticket ${route.params.ticketId}`
}

export const buildFetchTicketsParameters = (route: RouteLocationNormalizedLoaded | { query: string }) => {
  const { allAssigneeUsers } = storeToRefs(useTeamStore())

  const queryParams: Record<string, string | string[]> = {}

  Object.entries(route.query).forEach(([key, value]) => {
    if (!value) return

    if (key === 'assignedToId') {
      if (Array.isArray(value)) {
        const hasUnassigned = value.some(
          (v) => typeof v === 'string' && v[0]?.toLowerCase() === 'u' && v.toLowerCase().startsWith('unassigned'),
        )

        if (hasUnassigned) {
          queryParams[key] = 'null'
        } else {
          const ids = value.map((v) => {
            const foundUser = allAssigneeUsers.value?.find((user: Assignee) => user.name === v)

            return foundUser ? foundUser.id : v
          })

          queryParams[key] = ids
        }
      } else if (typeof value === 'string') {
        let currentUser = null
        const foundUser = allAssigneeUsers.value?.find((user: Assignee) => user.name === value)

        if (foundUser) {
          currentUser = foundUser
        } else {
          currentUser = { name: value, id: value }
        }
        if (currentUser && value[0]?.toLowerCase() !== 'u' && value.toLowerCase() !== 'unassigned') {
          queryParams[key] = currentUser.id
        } else if (value[0]?.toLowerCase() === 'u' || value.toLowerCase() === 'unassigned') {
          queryParams[key] = 'null'
        }
      }
    } else {
      queryParams[key] = value as string
    }
  })

  return queryParams
}

export const filterValidFiles = (filesArray: File[]): File[] => {
  const { toast } = useToast()
  const explicitTypes = ['application/pdf', 'text/csv']
  const patterns = [/^image\//, /^video\//]

  const validFiles = filesArray.filter((file) => explicitTypes.includes(file.type) || patterns.some((p) => p.test(file.type)))

  const invalidFiles = filesArray.filter(
    (file) => !explicitTypes.includes(file.type) && !patterns.some((p) => p.test(file.type)),
  )

  invalidFiles.forEach((file) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || file.type

    toast({
      title: `Extension ${ext} is not accepted`,
      variant: 'destructive',
    })
  })

  return validFiles
}

const HEX_SHORT_REGEX = /^#([0-9A-F]{3})$/i
const HEX_FULL_REGEX = /^#([0-9A-F]{6})$/i

export function adjustHexLightness(hex: string, factor: number): string {
  if (!isValidHex(hex)) {
    hex = '#18181B'
  }

  const normalized = normalizeHex(hex)
  const { r, g, b } = hexToRgb(normalized)

  const { h, s, l } = rgbToHsl(r, g, b)
  const newL = clamp(adjustLightness(l, factor), 0, 1)
  const { r: r2, g: g2, b: b2 } = hslToRgb(h, s, newL)

  return rgbToHex(r2, g2, b2)
}

function isValidHex(hex: string): boolean {
  return HEX_SHORT_REGEX.test(hex) || HEX_FULL_REGEX.test(hex)
}

function normalizeHex(hex: string): string {
  const matchShort = HEX_SHORT_REGEX.exec(hex)

  if (matchShort) {
    return (
      '#' +
      matchShort[1]
        .split('')
        .map((c) => c + c)
        .join('')
    )
  }
  const matchFull = HEX_FULL_REGEX.exec(hex)

  if (matchFull) {
    return '#' + matchFull[1]
  }
  throw new Error(`Invalid hex: ${hex}`)
}

export const hexToRgb = (h: string) => {
  let r: any = 0
  let g: any = 0
  let b: any = 0

  // 3 digits
  if (h.length == 4) {
    r = '0x' + h[1] + h[1]
    g = '0x' + h[2] + h[2]
    b = '0x' + h[3] + h[3]

    // 6 digits
  } else if (h.length == 7) {
    r = '0x' + h[1] + h[2]
    g = '0x' + h[3] + h[4]
    b = '0x' + h[5] + h[6]
  }

  return {
    r,
    g,
    b,
  }
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => n.toString(16).padStart(2, '0')

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

export const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  let h = 0

  if (delta) {
    if (max === r) {
      h = ((g - b) / delta) % 6
    } else if (max === g) {
      h = (b - r) / delta + 2
    } else {
      h = (r - g) / delta + 4
    }
    h = Math.round(h * 60)
    if (h < 0) h += 360
  }

  const l = (max + min) / 2
  const s = delta ? delta / (1 - Math.abs(2 * l - 1)) : 0

  return { h, s, l }
}

function hslToRgb(h: number, s: number, l: number) {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2

  let r1 = 0
  let g1 = 0
  let b1 = 0

  if (h < 60) {
    r1 = c
    g1 = x
  } else if (h < 120) {
    r1 = x
    g1 = c
  } else if (h < 180) {
    g1 = c
    b1 = x
  } else if (h < 240) {
    g1 = x
    b1 = c
  } else if (h < 300) {
    r1 = x
    b1 = c
  } else {
    r1 = c
    b1 = x
  }

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  }
}

export const RGBToHSL = (r: any, g: any, b: any) => {
  // Make r, g, and b fractions of 1
  r /= 255
  g /= 255
  b /= 255

  // Find greatest and smallest channel values
  const cmin = Math.min(r, g, b)
  const cmax = Math.max(r, g, b)
  const delta = cmax - cmin
  let h = 0
  let s = 0
  let l = 0

  if (delta == 0) h = 0
  // Red is max
  else if (cmax == r) h = ((g - b) / delta) % 6
  // Green is max
  else if (cmax == g) h = (b - r) / delta + 2
  // Blue is max
  else h = (r - g) / delta + 4

  h = Math.round(h * 60)

  // Make negative hues positive behind 360°
  if (h < 0) h += 360

  // Calculate lightness
  l = (cmax + cmin) / 2

  // Calculate saturation
  s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

  // Multiply l and s by 100
  s = +(s * 100).toFixed(1)
  l = +(l * 100).toFixed(1)

  return 'hsl(' + h + ',' + s + '%,' + l + '%)'
}

function adjustLightness(l: number, factor: number): number {
  return factor > 1 ? l * (2 - factor) : l + (1 - l) * (1 - factor)
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export const darkenRgbColor = (rgbString: string, factor: number = 0.7): string => {
  console.log('🚀 ~ darkenRgbColor ~ rgbString:', rgbString)
  if (!rgbString || typeof rgbString !== 'string') {
    return rgbString
  }

  // Extract RGB values using regex
  const rgbMatch = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)/.exec(rgbString)

  if (!rgbMatch) {
    console.warn(`Invalid RGB format: ${rgbString}`)

    return rgbString
  }

  const r = parseInt(rgbMatch[1], 10)
  const g = parseInt(rgbMatch[2], 10)
  const b = parseInt(rgbMatch[3], 10)
  const alpha = rgbMatch[4] ? parseFloat(rgbMatch[4]) : null

  // Validate RGB values
  if (isNaN(r) || isNaN(g) || isNaN(b) || r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
    console.warn(`Invalid RGB values: ${rgbString}`)

    return rgbString
  }

  // Clamp factor between 0 and 1
  const clampedFactor = Math.max(0, Math.min(1, factor))

  // Darken by reducing each RGB component
  const darkerR = Math.round(r * (1 - clampedFactor))
  const darkerG = Math.round(g * (1 - clampedFactor))
  const darkerB = Math.round(b * (1 - clampedFactor))

  // Return in same format as input (rgb or rgba)
  if (alpha !== null) {
    return `rgba(${darkerR}, ${darkerG}, ${darkerB}, ${alpha})`
  } else {
    return `rgb(${darkerR}, ${darkerG}, ${darkerB})`
  }
}

const componentMap: Record<string, Component> = {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpFromDot,
  ChevronsUpDown,
  Circle,
  CircleCheck,
  CircleChevronDown,
  CircleMinus,
  CircleX,
  GalleryThumbnails,
  Mail,
  MessageCircle,
  NotepadText,
  Phone,
  Timer,
  Webhook,
  History,
  Hourglass,
  TimerReset,
  TimerOff,
  ClockArrowUp,
  ClockAlert,
  ClockArrowDown,
  ArrowRightLeft,
  CloudAlert,
  Eye,
  FileWarning,
  OctagonAlert,
  OctagonX,
  Siren,
  SquarePlus,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  ArrowBigDownDash,
  ChevronDown,
  ChevronsDown,
  ShieldAlert,
  ShieldBan,
  ArrowBigUp,
  ArrowBigUpDash,
}

export const getIconComponent = (icon: string): Component | string => {
  return componentMap[icon] || icon
}

export const eventBus = mitt()

/**
 * Cache to store already calculated colors for companies and contacts
 */
const colorCache = new Map<string, string>()

/**
 * Generates a consistent color from a string (company ID, contact ID, or name)
 * @param str String to generate color from (preferably an ID)
 * @returns A Tailwind color class name
 */
export const getConsistentColor = (str: string): string => {
  if (!str) return 'bg-slate-400'

  // Check cache first for performance
  if (colorCache.has(str)) {
    const cachedColor = colorCache.get(str)

    // This is safe because we just checked it exists with colorCache.has()
    return cachedColor || 'bg-slate-400'
  }

  // List of Tailwind colors to use (avoiding very light ones)
  const colors = [
    'bg-red-500',
    'bg-blue-500',
    'bg-emerald-500',
    'bg-amber-500',
    'bg-violet-500',
    'bg-sky-500',
    'bg-orange-500',
    'bg-teal-500',
    'bg-rose-500',
    'bg-indigo-500',
    'bg-lime-500',
    'bg-cyan-500',
    'bg-pink-500',
    'bg-fuchsia-500',
    'bg-green-500',
    'bg-blue-600',
    'bg-teal-600',
    'bg-green-600',
    'bg-yellow-500',
    'bg-red-600',
    'bg-orange-600',
    'bg-amber-600',
    'bg-emerald-600',
    'bg-sky-600',
    'bg-indigo-600',
    'bg-violet-600',
    'bg-red-600',
    'bg-fuchsia-600',
    'bg-pink-600',
    'bg-rose-600',
  ]

  // Simple hash function
  let hash = 0

  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    // Convert to 32bit integer
    hash = hash & hash
  }

  // Make sure it's positive and get an index within the colors array
  const index = Math.abs(hash) % colors.length
  const result = colors[index]

  // Store in cache for future use
  colorCache.set(str, result)

  return result
}

/**
 * Cache to store contact initials
 */
const initialsCache = new Map<string, string>()

/**
 * Generates consistent initials from a name and caches the result
 * @param name Full name to generate initials from
 * @returns Initials (1-2 characters)
 */
export const getContactInitials = (name: string): string => {
  if (!name) return ''

  // Check cache first for performance
  if (initialsCache.has(name)) {
    const cachedInitials = initialsCache.get(name)

    return cachedInitials || ''
  }

  // Generate initials from name
  const nameArray = name.split(' ')
  let initials = ''

  if (nameArray.length >= 2) {
    // Get first letter of first and last name
    initials = nameArray[0].charAt(0).toUpperCase() + nameArray[1].charAt(0).toUpperCase()
  } else if (nameArray.length === 1) {
    // Get first letter of only name
    initials = nameArray[0].charAt(0).toUpperCase()
    // If name is long enough, add second letter
    if (nameArray[0].length > 1) {
      initials += nameArray[0].charAt(1).toUpperCase()
    }
  }

  // Store in cache for future use
  initialsCache.set(name, initials)

  return initials
}

export const getBadgeVariant = (priority: any, percentage: number) => {
  if (percentage == null || isNaN(percentage) || percentage === 0) {
    return priority?.defaultBadgeColor || 'text-zinc-900 border-zinc-900'
  }

  if (priority?.slaBadgeSteps && Array.isArray(priority.slaBadgeSteps) && priority.slaBadgeSteps.length) {
    const steps = [...priority.slaBadgeSteps].sort((a: any, b: any) => b.threshold - a.threshold)

    for (const step of steps) {
      if (percentage >= step.threshold) {
        return step.color
      }
    }

    return priority?.defaultBadgeColor || 'text-zinc-900 border-zinc-900'
  }

  return priority?.defaultBadgeColor || 'text-zinc-900 border-zinc-900'
}

export const formatPhoneNumber = (phoneNumberString: string | null) => {
  if (!phoneNumberString) return '--'

  const cleaned = ('' + phoneNumberString).replace(/\D/g, '')
  const phoneRegex = /^(1)?(\d{3})(\d{3})(\d{4})$/
  const match = phoneRegex.exec(cleaned)

  if (match) {
    const intlCode = match[1] ? '+1 ' : ''

    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
  }

  return phoneNumberString
}

export const formatTimeInsight = (seconds: number | null | undefined): string => {
  if (!seconds || isNaN(seconds) || seconds < 0) return '0m'

  const SECONDS_PER_DAY = 24 * 3600
  const SECONDS_PER_HOUR = 3600
  const SECONDS_PER_MINUTE = 60

  const days = Math.floor(seconds / SECONDS_PER_DAY)
  const hours = Math.floor((seconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR)
  const minutes = Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)

  const parts: string[] = []

  if (days > 0) parts.push(`${days}d`)
  if (hours > 0 || days > 0) parts.push(`${hours}h`)
  if (minutes > 0 || (!days && !hours)) parts.push(`${minutes}m`)

  return parts.join(' ')
}

export const normalizePhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return phoneNumber
  const trimmed = phoneNumber.trim()

  return trimmed.startsWith('+') ? trimmed : `+${trimmed}`
}

export const formatTimeSpent = (timeInSeconds: number, statusName: string): string => {
  if (!timeInSeconds || timeInSeconds === 0) return ''

  const days = Math.floor(timeInSeconds / 86400)
  const hours = Math.floor((timeInSeconds % 86400) / 3600)
  const minutes = Math.floor((timeInSeconds % 3600) / 60)
  const text = statusName.length > 0 ? `Time in ${statusName}` : ''

  if (days > 0) {
    const h = hours.toString().padStart(1, '0')
    const m = minutes.toString().padStart(2, '0')

    return `${text} ${days} day${days > 1 ? 's' : ''} and ${h}:${m} hours`
  } else if (hours > 0) {
    const h = hours.toString().padStart(1, '0')
    const m = minutes.toString().padStart(2, '0')

    return `${text} ${h}:${m} hours`
  } else if (minutes > 0) {
    return `${text} ${minutes} minute${minutes > 1 ? 's' : ''}`
  } else {
    return `${text} less than 1 minute`
  }
}
